import { Link, useNavigate, useLocation } from "@tanstack/react-router";
import {
  Globe,
  Menu,
  UserCircle,
  User,
  LogOut,
  Wallet,
  Crown,
} from "lucide-react";
import { memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import logo from "../assets/logo.svg";
import { useHeader } from "../hooks/useHeader";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "~/components/ui/popover";

// Memoized: pages re-render their whole tree on every editor keystroke
export const Header = memo(function Header() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    getLanguagePath,
    getPagePath,
    handleLanguageClick,
    user,
    telegramWidgetRef,
    handleLogout,
    setShowLoginWidget,
    showLoginWidget,
  } = useHeader();
  const location = useLocation();
  const [isUserPopoverOpen, setIsUserPopoverOpen] = useState(false);

  const currentLang = (() => {
    const segments = location.pathname.split("/").filter(Boolean);
    const langs = ["uz", "ru", "en"];
    return langs.includes(segments[0]) ? segments[0] : "oz";
  })();

  // Hide widget when user successfully logs in (but keep popover open for logout option)
  useEffect(() => {
    if (user && showLoginWidget) {
      setShowLoginWidget(false);
    }
  }, [user, showLoginWidget]);

  return (
    <header className="bg-blue-800 p-4 flex justify-between items-center">
      <a href="/" className="text-white">
        <img src={logo} alt="Lotin.uz" />
      </a>

      <nav className="flex gap-2 lg:gap-4 items-center">
        <div className="hidden lg:flex gap-4">
          <Link to={getPagePath("chat")} className="text-white">
            {t("header.chat")}
          </Link>
          <Link
            to={getPagePath("uzbek-transliteration-api")}
            className="text-white"
          >
            {t("header.api")}
          </Link>
          <Link to={getPagePath("about")} className="text-white">
            {t("header.aboutProject")}
          </Link>
          <Link to={getPagePath("faq")} className="text-white">
            {t("header.faq")}
          </Link>
          <Link to={getPagePath("donate")} className="text-white">
            {t("header.gratitude")}
          </Link>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="secondary"
              size="icon"
              aria-label={t("aria.selectLanguage")}
            >
              <Globe />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuCheckboxItem
              checked={currentLang === "oz"}
              onSelect={() => handleLanguageClick("oz")}
            >
              <Link to={getLanguagePath("oz")}>O&lsquo;zbekcha</Link>
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={currentLang === "uz"}
              onSelect={() => handleLanguageClick("uz")}
            >
              <Link to={getLanguagePath("uz")}>Ўзбекча</Link>
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={currentLang === "ru"}
              onSelect={() => handleLanguageClick("ru")}
            >
              <Link to={getLanguagePath("ru")}>Русский</Link>
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={currentLang === "en"}
              onSelect={() => handleLanguageClick("en")}
            >
              <Link to={getLanguagePath("en")}>English</Link>
            </DropdownMenuCheckboxItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {user ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" size="icon" aria-label={user.name}>
                <span className="text-sm font-bold">
                  {user.name.charAt(0).toUpperCase()}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>{user.name}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => navigate({ to: getPagePath("profile") })}
              >
                <User />
                {t("Profile")}
              </DropdownMenuItem>
              {/* TODO: Hisob va Obuna bandlari vaqtincha o'chirilgan (local test uchun).
                  Qaytarish uchun quyidagi blokni comment'dan chiqaring. */}
              {/* <DropdownMenuItem
                onClick={() => navigate({ to: getPagePath("balance") })}
              >
                <Wallet />
                {t("header.balance", "Hisob")}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => navigate({ to: getPagePath("subscription") })}
              >
                <Crown />
                {t("header.subscription", "Obuna")}
              </DropdownMenuItem> */}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                variant="destructive"
                onClick={() => {
                  handleLogout();
                  // Leave auth-only pages (profile, balance, ...) on logout;
                  // getPagePath("") keeps the active language prefix.
                  navigate({ to: getPagePath("") });
                }}
              >
                <LogOut />
                {t("Exit")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Button
            variant="secondary"
            size="icon"
            aria-label={t("aria.login")}
            onClick={() => navigate({ to: getPagePath("login") })}
          >
            <UserCircle />
          </Button>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="secondary"
              size="icon"
              className="lg:hidden"
              aria-label={t("aria.menu")}
            >
              <Menu />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild>
              <Link to={getPagePath("chat")}>{t("header.chat")}</Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to={getPagePath("uzbek-transliteration-api")}>
                {t("header.api")}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to={getPagePath("about")}>{t("header.aboutProject")}</Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to={getPagePath("faq")}>{t("header.faq")}</Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to={getPagePath("donate")}>{t("header.gratitude")}</Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </nav>
    </header>
  );
});
